import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface AutoRefreshConfig {
  enabled: boolean;
  interval: number; // in milliseconds
}

interface AutoRefreshToggleProps {
  value: AutoRefreshConfig;
  onChange: (config: AutoRefreshConfig) => void;
  onManualRefresh: () => void;
  lastUpdated?: Date;
  className?: string;
}

const REFRESH_INTERVALS = [
  { value: 5000, label: '5s' },
  { value: 10000, label: '10s' },
  { value: 30000, label: '30s' },
  { value: 60000, label: '1m' },
  { value: 300000, label: '5m' },
];

export default function AutoRefreshToggle({
  value,
  onChange,
  onManualRefresh,
  lastUpdated,
  className = '',
}: AutoRefreshToggleProps) {
  const { t } = useTranslation(['common']);
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    onChange({
      ...value,
      enabled: !value?.enabled,
    });
  };

  const handleIntervalChange = (interval: number) => {
    onChange({
      enabled: value?.enabled ?? false,
      interval,
    });
    setIsOpen(false);
  };

  const getIntervalLabel = () => {
    const interval = REFRESH_INTERVALS.find(i => i.value === value?.interval);
    return interval ? interval.label : '30s';
  };

  const getLastUpdatedText = () => {
    if (!lastUpdated) return t('common:autoRefresh.never');

    const now = new Date();
    const diff = now.getTime() - lastUpdated.getTime();

    if (diff < 1000) return t('common:autoRefresh.justNow');
    if (diff < 60000) return t('common:autoRefresh.secondsAgo', { count: Math.floor(diff / 1000) });
    if (diff < 3600000) return t('common:autoRefresh.minutesAgo', { count: Math.floor(diff / 60000) });
    return lastUpdated.toLocaleTimeString();
  };

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {/* Auto-refresh Toggle */}
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
          {t('common:autoRefresh.label')}
        </label>
        <button
          onClick={handleToggle}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            value?.enabled
              ? 'bg-indigo-600'
              : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              value?.enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Interval Selector */}
      {value?.enabled && (
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center space-x-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <span>{t('common:autoRefresh.every')} {getIntervalLabel()}</span>
            <svg
              className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown */}
          {isOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsOpen(false)}
              />
              <div className="absolute right-0 z-20 mt-2 w-24 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                {REFRESH_INTERVALS.map((interval) => (
                  <button
                    key={interval.value}
                    onClick={() => handleIntervalChange(interval.value)}
                    className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      value?.interval === interval.value
                        ? 'bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                        : 'text-gray-700 dark:text-gray-300'
                    } ${
                      interval.value === REFRESH_INTERVALS[0].value
                        ? 'rounded-t-lg'
                        : ''
                    } ${
                      interval.value === REFRESH_INTERVALS[REFRESH_INTERVALS.length - 1].value
                        ? 'rounded-b-lg'
                        : ''
                    }`}
                  >
                    {interval.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Manual Refresh Button */}
      <button
        onClick={onManualRefresh}
        className="flex items-center space-x-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        title={t('common:autoRefresh.refreshNow')}
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        <span>{t('common:autoRefresh.refresh')}</span>
      </button>

      {/* Last Updated */}
      <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        <span className="hidden sm:inline">{t('common:autoRefresh.lastUpdated')}: </span>
        <span className="font-medium">{getLastUpdatedText()}</span>
      </div>
    </div>
  );
}
